import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { Package, Lock, Mail } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@lager.de");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Forgot-password dialog state
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const handleForgot = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      setForgotSent(true);
      toast.success("Falls die E-Mail existiert, wurde ein Reset-Link gesendet.");
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      toast.error(msg);
    } finally {
      setForgotLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Willkommen zurück.");
      navigate("/");
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#F4F4F5]">
      {/* Industrial side panel */}
      <div className="hidden lg:flex relative bg-black text-white p-12 flex-col justify-between overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1585569695919-db237e7cc455?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/60 to-black/90" />
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-12">
            <img
              src="/logo-celentano.png"
              alt="Celentano"
              className="w-14 h-14 object-contain bg-white p-1 shrink-0"
            />
            <div>
              <div className="font-display text-2xl uppercase leading-none">Celentano</div>
              <div className="text-[11px] tracking-[0.3em] text-zinc-400 mono mt-1">
                GARAGENTORE · TÜREN · MONTAGEN
              </div>
            </div>
          </div>
          <div className="max-w-md">
            <h1 className="font-display text-5xl uppercase leading-[0.9] mb-6">
              Werkstatt
              <br />
              <span className="text-[#F97316]">unter Kontrolle.</span>
            </h1>
            <p className="text-zinc-300 leading-relaxed text-base">
              Regale, Fächer, Plätze. EAN-Etiketten drucken, Bestand scannen, Lagerplan
              visualisieren — alles in einer App.
            </p>
          </div>
        </div>
        <div className="relative z-10 mono text-[11px] tracking-[0.25em] text-zinc-500 flex justify-between">
          <span>STATUS · ONLINE</span>
          <span>v 1.0.0</span>
        </div>
      </div>

      {/* Login form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm animate-slide-up">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src="/logo-celentano.png" alt="Celentano" className="w-12 h-12 object-contain bg-black p-0.5" />
            <div>
              <div className="font-display text-base uppercase leading-none">Celentano</div>
              <div className="text-[10px] tracking-[0.2em] text-zinc-500 mono mt-1">
                LAGERVERWALTUNG
              </div>
            </div>
          </div>

          <div className="mono text-[11px] tracking-[0.25em] text-zinc-500 mb-3">01 · LOGIN</div>
          <h2 className="font-display text-3xl uppercase mb-2">Anmelden</h2>
          <p className="text-sm text-zinc-600 mb-8">
            Greife auf dein Lager zu — mit E-Mail und Passwort.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider font-semibold">
                E-Mail
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                className="mt-1.5 rounded-sm border-zinc-400 focus-visible:ring-[#F97316] focus-visible:ring-2 focus-visible:ring-offset-0"
                placeholder="admin@lager.de"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider font-semibold">
                Passwort
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                className="mt-1.5 rounded-sm border-zinc-400 focus-visible:ring-[#F97316] focus-visible:ring-2 focus-visible:ring-offset-0"
                placeholder="••••••••"
              />
            </div>

            {err && (
              <div
                data-testid="login-error"
                className="border-l-2 border-red-600 bg-red-50 px-3 py-2 text-sm text-red-700"
              >
                {err}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold rounded-sm h-11 tracking-wide"
            >
              <Lock className="w-4 h-4 mr-2" />
              {loading ? "ANMELDEN…" : "ANMELDEN"}
            </Button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  setForgotEmail(email);
                  setForgotSent(false);
                  setForgotOpen(true);
                }}
                data-testid="forgot-password-link"
                className="text-sm text-zinc-600 hover:text-[#F97316] hover:underline"
              >
                Passwort vergessen?
              </button>
            </div>
          </form>

          <div className="mt-10 pt-6 border-t border-zinc-200">
            <div className="mono text-[10px] tracking-[0.25em] text-zinc-500 mb-1">
              STANDARD-ZUGANG
            </div>
            <div className="mono text-xs text-zinc-700">
              admin@lager.de · 123456
            </div>
            <div className="mt-3 text-sm">
              Noch kein Account?{" "}
              <Link to="/registrieren" className="text-[#F97316] font-medium hover:underline" data-testid="register-link">
                Jetzt registrieren →
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="sm:max-w-md" data-testid="forgot-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl uppercase">Passwort vergessen?</DialogTitle>
            <DialogDescription>
              Gib deine E-Mail-Adresse ein. Wir senden dir einen Link zum Zurücksetzen — gültig für 30 Minuten.
            </DialogDescription>
          </DialogHeader>

          {forgotSent ? (
            <div className="border-l-2 border-green-600 bg-green-50 px-4 py-3 text-sm text-green-800">
              <strong>E-Mail unterwegs.</strong>
              <br />
              Falls die Adresse bei uns hinterlegt ist, erhältst du gleich einen Link. Bitte auch den Spam-Ordner prüfen.
            </div>
          ) : (
            <form onSubmit={handleForgot} className="space-y-4">
              <div>
                <Label htmlFor="forgot-email" className="text-xs uppercase tracking-wider font-semibold">
                  E-Mail
                </Label>
                <Input
                  id="forgot-email"
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                  data-testid="forgot-email-input"
                  className="mt-1.5 rounded-sm border-zinc-400 focus-visible:ring-[#F97316] focus-visible:ring-2 focus-visible:ring-offset-0"
                  placeholder="deine@email.de"
                />
              </div>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setForgotOpen(false)}
                  data-testid="forgot-cancel-button"
                >
                  Abbrechen
                </Button>
                <Button
                  type="submit"
                  disabled={forgotLoading}
                  data-testid="forgot-submit-button"
                  className="bg-[#F97316] hover:bg-[#EA580C] text-white"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  {forgotLoading ? "SENDEN…" : "RESET-LINK SENDEN"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
